import { ASAAS_ENABLED } from '../config/constants';
import type { IPublicPlan, SubscriptionBillingCycle } from '../services/subscriptionPlans/types';
import type { AsaasBillingType, IPaymentCheckoutResponse } from '../services/payments/types';
import type { PlanBillingPreference } from './planBillingPricing';
import { validateSubscriptionPlanSlug } from './validateSubscriptionPlanSlug';

/** Checkout Asaas disponível — espelha `ASAAS_ENABLED` (só `false` desliga). */
export const isCheckoutAvailable = (): boolean => ASAAS_ENABLED;

/** Guards, interceptors e banners de cobrança — desligados junto com o checkout. */
export const isBillingEnforcementEnabled = (): boolean => ASAAS_ENABLED;

/** Destino do CTA “Criar conta” / entrada pública de cadastro. */
export const getPublicSignupEntryPath = (): string =>
  isCheckoutAvailable() ? '/register' : '/escolher-plano?plan=starter';

export const PLAN_SIGNUP_INTENT_KEY = 'salespetro_plan_signup_intent';

export const CHECKOUT_SESSION_STORAGE_KEY = 'salespetro:checkout-session';

export const CHECKOUT_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/** @deprecated Use PLAN_SIGNUP_INTENT_KEY — mantido para migração de sessões antigas */
export const PENDING_CHECKOUT_PLAN_KEY = 'salespetro_pending_checkout_plan';

export type PlanSignupMode = 'trial' | 'subscribe';

export interface PlanSignupIntent {
  planSlug: string;
  mode: PlanSignupMode;
}

export interface ICheckoutSession {
  billingSubscriptionId: string;
  planSlug: string;
  billingType: AsaasBillingType;
  billingPreference: PlanBillingPreference;
  providerPaymentId?: string;
  createdAt: number;
  responseSnapshot: IPaymentCheckoutResponse;
}

export const DEFAULT_TRIAL_DURATION_DAYS = 7;

export const LEGACY_TRIAL_PLAN_SLUG = 'trial';

export const formatPlanPrice = (
  priceCents: number,
  cycle: SubscriptionBillingCycle = 'monthly'
): string => {
  const value = (priceCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
  return cycle === 'yearly' ? `${value}/ano` : `${value}/mês`;
};

export const isPaidPublicPlan = (plan: Pick<IPublicPlan, 'priceCents'>): boolean =>
  plan.priceCents != null && plan.priceCents > 0;

export const isCheckoutEligiblePlan = (plan: IPublicPlan): boolean =>
  plan.slug !== LEGACY_TRIAL_PLAN_SLUG && isPaidPublicPlan(plan);

export const isLegacyTrialPlanSlug = (slug: string): boolean =>
  slug.trim().toLowerCase() === LEGACY_TRIAL_PLAN_SLUG;

export const isTrialLikePublicPlan = (plan: IPublicPlan): boolean =>
  isLegacyTrialPlanSlug(plan.slug);

export const isValidPlanSignupMode = (mode: string | null | undefined): mode is PlanSignupMode =>
  mode === 'trial' || mode === 'subscribe';

const normalizePlanSlug = (slug: string): string | null => {
  const normalized = slug.trim().toLowerCase();
  if (!normalized || validateSubscriptionPlanSlug(normalized)) return null;
  return normalized;
};

const readStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

const readSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage;
};

const isAsaasBillingType = (value: unknown): value is AsaasBillingType =>
  value === 'PIX' || value === 'BOLETO' || value === 'CREDIT_CARD';

const isPlanBillingPreference = (value: unknown): value is PlanBillingPreference =>
  value === 'monthly' || value === 'annual_commitment';

const parseCheckoutSession = (raw: string | null): ICheckoutSession | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ICheckoutSession>;
    const planSlug = normalizePlanSlug(parsed.planSlug ?? '');
    if (
      !planSlug ||
      typeof parsed.billingSubscriptionId !== 'string' ||
      !parsed.billingSubscriptionId ||
      typeof parsed.createdAt !== 'number' ||
      !isAsaasBillingType(parsed.billingType) ||
      !isPlanBillingPreference(parsed.billingPreference) ||
      !parsed.responseSnapshot ||
      typeof parsed.responseSnapshot !== 'object'
    ) {
      return null;
    }

    if (Date.now() - parsed.createdAt > CHECKOUT_SESSION_TTL_MS) {
      return null;
    }

    return {
      billingSubscriptionId: parsed.billingSubscriptionId,
      planSlug,
      billingType: parsed.billingType,
      billingPreference: parsed.billingPreference,
      providerPaymentId:
        typeof parsed.providerPaymentId === 'string' ? parsed.providerPaymentId : undefined,
      createdAt: parsed.createdAt,
      responseSnapshot: parsed.responseSnapshot as IPaymentCheckoutResponse,
    };
  } catch {
    return null;
  }
};

export const setCheckoutSessionFromResponse = (response: IPaymentCheckoutResponse): void => {
  const storage = readSessionStorage();
  const planSlug = normalizePlanSlug(response.planSlug);
  if (!storage || !planSlug) return;

  const session: ICheckoutSession = {
    billingSubscriptionId: response.billingSubscriptionId,
    planSlug,
    billingType: response.billingType,
    billingPreference: response.billingPreference,
    providerPaymentId: response.providerPaymentId,
    createdAt: Date.now(),
    responseSnapshot: response,
  };

  storage.setItem(CHECKOUT_SESSION_STORAGE_KEY, JSON.stringify(session));
};

export const getCheckoutSession = (): ICheckoutSession | null => {
  const storage = readSessionStorage();
  if (!storage) return null;
  return parseCheckoutSession(storage.getItem(CHECKOUT_SESSION_STORAGE_KEY));
};

export const getCheckoutSessionForPlan = (planSlug: string): ICheckoutSession | null => {
  const session = getCheckoutSession();
  if (!session) return null;

  const normalized = normalizePlanSlug(planSlug);
  if (!normalized || session.planSlug !== normalized) return null;

  return session;
};

export const hasPendingCheckoutSession = (planSlug: string): boolean =>
  getCheckoutSessionForPlan(planSlug) != null;

export const clearCheckoutSession = (): void => {
  const storage = readSessionStorage();
  storage?.removeItem(CHECKOUT_SESSION_STORAGE_KEY);
};

const parsePlanSignupIntent = (raw: string | null): PlanSignupIntent | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PlanSignupIntent>;
    const planSlug = normalizePlanSlug(parsed.planSlug ?? '');
    if (!planSlug || !isValidPlanSignupMode(parsed.mode)) return null;
    return { planSlug, mode: parsed.mode };
  } catch {
    const legacySlug = normalizePlanSlug(raw);
    if (!legacySlug) return null;
    return { planSlug: legacySlug, mode: 'subscribe' };
  }
};

export const setPlanSignupIntent = (intent: PlanSignupIntent): void => {
  const storage = readStorage();
  const planSlug = normalizePlanSlug(intent.planSlug);
  if (!storage || !planSlug || !isValidPlanSignupMode(intent.mode)) return;

  storage.setItem(
    PLAN_SIGNUP_INTENT_KEY,
    JSON.stringify({ planSlug, mode: intent.mode } satisfies PlanSignupIntent)
  );
};

export const getPlanSignupIntent = (): PlanSignupIntent | null => {
  const storage = readStorage();
  if (!storage) return null;

  const fromIntent = parsePlanSignupIntent(storage.getItem(PLAN_SIGNUP_INTENT_KEY));
  if (fromIntent) return fromIntent;

  const legacySlug = normalizePlanSlug(storage.getItem(PENDING_CHECKOUT_PLAN_KEY) ?? '');
  if (!legacySlug) return null;
  return { planSlug: legacySlug, mode: 'subscribe' };
};

export const clearPlanSignupIntent = (): void => {
  const storage = readStorage();
  if (!storage) return;
  storage.removeItem(PLAN_SIGNUP_INTENT_KEY);
  storage.removeItem(PENDING_CHECKOUT_PLAN_KEY);
};

export const resolvePlanSignupIntent = (
  queryPlan: string | null,
  queryMode: string | null
): PlanSignupIntent | null => {
  const planFromQuery = normalizePlanSlug(queryPlan ?? '');
  const modeFromQuery = isValidPlanSignupMode(queryMode) ? queryMode : null;

  if (planFromQuery && modeFromQuery) {
    return { planSlug: planFromQuery, mode: modeFromQuery };
  }

  const stored = getPlanSignupIntent();
  if (!stored) return null;

  if (planFromQuery && stored.planSlug === planFromQuery) {
    return { planSlug: planFromQuery, mode: modeFromQuery ?? stored.mode };
  }

  if (planFromQuery && modeFromQuery) {
    return { planSlug: planFromQuery, mode: modeFromQuery };
  }

  if (!planFromQuery) return stored;

  return { planSlug: planFromQuery, mode: modeFromQuery ?? stored.mode };
};

export const buildChoosePlanPath = (slug: string): string =>
  `/escolher-plano?plan=${encodeURIComponent(slug)}`;

export const buildAssinarPath = (slug: string, mode: PlanSignupMode = 'subscribe'): string =>
  `/assinar?plan=${encodeURIComponent(slug)}&mode=${mode}`;

export const buildRegisterPathWithMode = (intent: PlanSignupIntent): string =>
  `/register?plan=${encodeURIComponent(intent.planSlug)}&mode=${intent.mode}`;

/** Legado: path helpers de subscribe open-access (fluxo público usa trial via `/planos`). */
export const getOpenAccessSignupMode = (): PlanSignupMode => 'subscribe';

export const buildOpenAccessRegisterPath = (planSlug: string): string =>
  buildRegisterPathWithMode({ planSlug, mode: getOpenAccessSignupMode() });

export const buildRegisterPathWithPlan = (slug: string, mode: PlanSignupMode = 'trial'): string =>
  buildRegisterPathWithMode({ planSlug: slug, mode });

export const buildVerifyEmailPath = (email: string, intent: PlanSignupIntent): string => {
  const params = new URLSearchParams({
    email,
    plan: intent.planSlug,
    mode: intent.mode,
  });
  return `/verificar-email?${params.toString()}`;
};

export const buildLoginPathWithIntent = (
  intent?: Partial<PlanSignupIntent> | null,
  options?: { activated?: boolean }
): string => {
  const resolved = intent?.planSlug
    ? resolvePlanSignupIntent(intent.planSlug, intent.mode ?? null)
    : getPlanSignupIntent();

  if (!resolved) {
    return options?.activated ? '/login?activated=1' : '/login';
  }

  const params = new URLSearchParams({
    plan: resolved.planSlug,
    mode: resolved.mode,
  });
  if (options?.activated) params.set('activated', '1');
  return `/login?${params.toString()}`;
};

/** @deprecated Use buildLoginPathWithIntent */
export const buildLoginPathWithPlan = (slug: string): string =>
  buildLoginPathWithIntent({ planSlug: slug, mode: 'subscribe' });

/** @deprecated Use buildLoginPathWithIntent */
export const buildLoginPathActivatedWithPlan = (planSlug?: string | null): string =>
  planSlug
    ? buildLoginPathWithIntent({ planSlug, mode: 'subscribe' }, { activated: true })
    : buildLoginPathWithIntent(null, { activated: true });

/** @deprecated Use setPlanSignupIntent */
export const setPendingCheckoutPlan = (slug: string): void => {
  setPlanSignupIntent({ planSlug: slug, mode: 'subscribe' });
};

/** @deprecated Use getPlanSignupIntent */
export const getPendingCheckoutPlan = (): string | null => getPlanSignupIntent()?.planSlug ?? null;

/** @deprecated Use clearPlanSignupIntent */
export const clearPendingCheckoutPlan = (): void => {
  clearPlanSignupIntent();
};

export const resolveCheckoutPlanSlug = (queryPlan: string | null): string | null => {
  const intent = resolvePlanSignupIntent(queryPlan, null);
  return intent?.planSlug ?? null;
};

export const resolveCheckoutIntent = (
  queryPlan: string | null,
  queryMode: string | null
): PlanSignupIntent | null => resolvePlanSignupIntent(queryPlan, queryMode);

export const buildPixQrDataUrl = (base64Image: string): string => {
  if (base64Image.startsWith('data:')) return base64Image;
  return `data:image/png;base64,${base64Image}`;
};

export const getPostLoginPath = (intent: PlanSignupIntent | null): string => {
  if (!intent) return '/dashboard/summary';
  if (intent.mode === 'subscribe') return buildAssinarPath(intent.planSlug, 'subscribe');
  return '/dashboard/summary';
};
